import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { useSEO } from "@/components/SEO";
import { FALLBACK_BLOGS } from "@/lib/content";
import { fetchBlogPosts, useWP } from "@/lib/wp";
import { formatDate } from "@/lib/format";

export default function BlogList() {
  const blogs = useWP(fetchBlogPosts, FALLBACK_BLOGS, []);
  const [activeCat, setActiveCat] = useState("All");
  const [params, setParams] = useSearchParams();
  const activeTag = params.get("tag") || "";
  const [query, setQuery] = useState(params.get("q") || "");

  useSEO({
    title: "Blog — Borrow Smarter, in Plain Language | Loan Salaah",
    description: "Original guides from Loan Salaah on home loans, credit scores, balance transfers and smart borrowing in India.",
  });

  const categories = useMemo(() => {
    const set = new Set();
    blogs.forEach((b) => (b.categories?.length ? b.categories : [b.category || "Guides"]).forEach((c) => c && set.add(c)));
    return ["All", ...Array.from(set)];
  }, [blogs]);

  const activeTagName = useMemo(() => {
    if (!activeTag) return "";
    for (const b of blogs) {
      const found = (b.tags || []).find((t) => t.slug === activeTag);
      if (found) return found.name;
    }
    return activeTag;
  }, [blogs, activeTag]);

  const filtered = useMemo(() => {
    let list = blogs;
    if (activeCat !== "All") {
      list = list.filter((b) => (b.categories || [b.category]).includes(activeCat));
    }
    if (activeTag) {
      list = list.filter((b) => (b.tags || []).some((t) => t.slug === activeTag || t.name === activeTag));
    }
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((b) =>
        `${b.title} ${b.excerpt}`.toLowerCase().includes(q)
      );
    }
    return list;
  }, [blogs, activeCat, activeTag, query]);

  const clearTag = () => {
    const next = {};
    if (query) next.q = query;
    setParams(next, { replace: true });
  };

  return (
    <div data-testid="blog-page" className="pb-24 pt-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <div className="flex items-center gap-4">
            <span className="brass-rule" aria-hidden="true" />
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">The Salaah desk</span>
          </div>
          <h1 className="mt-5 font-serif text-4xl font-semibold tracking-tight text-concordia sm:text-5xl lg:text-6xl">
            Money sense, written for humans.
          </h1>
          <p className="mt-5 text-base leading-relaxed text-slate">
            Original Loan Salaah guides on borrowing, credit scores and loan planning — no jargon, no selling.
          </p>
        </Reveal>

        <Reveal delay={0.06} className="mt-10">
          <div className="relative max-w-md">
            <Search className="pointer-events-none absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate" />
            <input
              type="search"
              data-testid="blog-search-input"
              placeholder="Search articles — try “CIBIL” or “balance transfer”"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full rounded-full border border-sand bg-white py-3.5 pl-12 pr-11 text-sm text-charcoal outline-none transition-colors placeholder:text-slate/60 focus:border-brass"
            />
            {query && (
              <button
                data-testid="blog-search-clear"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="absolute right-4 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-slate transition-colors hover:text-concordia"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.08} className="mt-6 flex flex-wrap items-center gap-2" data-testid="blog-category-filters">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`blog-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setActiveCat(c)}
              className={`rounded-full px-4 py-2 text-xs font-semibold transition-all duration-300 ${
                activeCat === c
                  ? "bg-concordia text-ivory"
                  : "border border-sand bg-white text-charcoal/70 hover:border-concordia/40 hover:text-concordia"
              }`}
            >
              {c}
            </button>
          ))}
          {activeTag && (
            <button
              data-testid="blog-tag-filter-active"
              onClick={clearTag}
              className="inline-flex items-center gap-2 rounded-full bg-brass/15 px-4 py-2 text-xs font-semibold text-concordia transition-colors hover:bg-brass/25"
            >
              #{activeTagName}
              <X className="h-3 w-3" />
            </button>
          )}
        </Reveal>

        <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((b) => (
            <StaggerItem key={b.id || b.slug}>
              <Link to={`/blog/${b.slug}`} data-testid={`blog-card-${b.slug}`}
                className="group flex h-full flex-col overflow-hidden rounded-3xl border border-sand bg-white transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_28px_60px_rgba(30,74,61,0.14)]">
                {b.image && (
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={b.image} alt={b.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                )}
                <div className="flex flex-1 flex-col p-6">
                  <p className="font-mono text-[0.62rem] uppercase tracking-[0.25em] text-brass">{b.categories?.[0] || b.category || "Guides"}</p>
                  <h2 className="mt-3 font-serif text-xl font-semibold leading-snug text-concordia">{b.title}</h2>
                  <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-slate">{b.excerpt}</p>
                  {b.tags?.length > 0 && (
                    <span className="mt-4 flex flex-wrap gap-2">
                      {b.tags.slice(0, 3).map((t) => (
                        <span
                          key={t.slug}
                          role="button"
                          tabIndex={0}
                          data-testid={`blog-card-tag-${t.slug}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setParams({ tag: t.slug }, { replace: true });
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              e.stopPropagation();
                              setParams({ tag: t.slug }, { replace: true });
                            }
                          }}
                          className="cursor-pointer rounded-full border border-sand px-3 py-1 text-[0.65rem] font-semibold text-slate transition-colors hover:border-concordia hover:text-concordia"
                        >
                          #{t.name}
                        </span>
                      ))}
                    </span>
                  )}
                  <p className="mt-4 text-xs text-slate/70">
                    {b.authorName || b.author ? `${b.authorName || b.author} · ` : ""}{formatDate(b.date)}
                  </p>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>

        {filtered.length === 0 && (
          <div className="mt-16 text-center" data-testid="blog-empty">
            <p className="font-serif text-2xl font-semibold text-concordia">Nothing matches that yet.</p>
            <p className="mt-2 text-sm text-slate">Try a different search or category — new articles land regularly.</p>
          </div>
        )}
      </div>
    </div>
  );
}
